import * as React from "react";
import { useEffect, useState } from "react";
import { useModal } from "../../../../utils/custom-hooks";
import { ServiceProviderTemplate } from "../../../template/service-provider/default-template";
import { GroupNameModal } from "../../../organisms/common/group-name-modal";
import { connectPageService } from "../../../../application/domain/base/base-application-service";
import { Link, useLocation, useParams } from "react-router-dom";
import { ServiceProviderTabs } from "../../../molecules/service-provider-tab";
import {
  GroupsService,
  GroupsServiceProps,
} from "../../../../application/service/smart-apply/groups/groups-service";
import { ConfirmationModal } from "../../../organisms/smart-apply/modals/confirmation-modal";
import { ServiceProviderR } from "../routes";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import { BatchImportGroupModal } from "../../../organisms/common/batch-import-group-modal";
import { useFeatureFlag_IgnoreAclSettingByGroup } from "../../../../feature-flags";
import {
  PrimaryButton,
  SecondaryButton,
  TextButton,
} from "@graffer-inc/console-design-kit";
import { Dropdown } from "../../../molecules/dropdown";
import OutsideClickHandler from "@graffer-inc/react-outside-click-handler";
import { convertToFormattedId } from "@graffer-inc/cloud-gov-core";
import { Pagination } from "@graffer-inc/console-design-kit";

export const OrganizationGroupsList: React.FunctionComponent<
  GroupsServiceProps
> = ({
  isLoading,
  isBatchImporting,
  groups,
  orgSlug,
  paging,
  query,
  setPage,
  movePage,
  setQuery,
  fetchOrganization,
  fetchGroups,
  searchGroups,
  onCreateGroup,
  onUpdateGroup,
  importByCsv,
  updateByCsv,
  downloadTemplateCsv,
  onDownloadCsv,
}) => {
  const { page, name } = parseQueryOfLocation(useLocation()) as {
    page: string | undefined;
    name: string | undefined;
  };
  const { id: orgId } = useParams<{ id: string }>();
  const groupNameModal = useModal(false);
  const confirmationModal = useModal(false);
  const csvImportModal = useModal(false);
  const csvUpdateModal = useModal(false);
  const dropdownModal = useModal(false);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [isNewGroup, setIsNewGroup] = useState(false);

  useEffect(() => {
    fetchOrganization(orgId);
    setPage(parseInt(page!, 10) || 1);
    fetchGroups(orgId);
  }, [setPage, page, name, orgId, fetchGroups, fetchOrganization]);
  const canIgnoreAclSetting = useFeatureFlag_IgnoreAclSettingByGroup(orgSlug);

  if (!groups) {
    return null;
  }

  return (
    <ServiceProviderTemplate
      breadcrumbs={[{ to: ServiceProviderR.Home.link({}), title: "Home" }]}
    >
      <div className="has-text-left">
        <div className="columns is-centered">
          <div className="column">
            <ServiceProviderTabs orgId={orgId} />
            <nav className="level">
              <div className="level-left">
                <div className="level-item">
                  <div className="field has-addons">
                    <p
                      className="control has-icons-left has-icons-right"
                      style={{ minWidth: "280px" }}
                    >
                      <input
                        className="input"
                        placeholder="グループ名で検索"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        style={{
                          fontSize: "14px",
                        }}
                      />
                      <span className="icon is-small is-left">
                        <i className="fas fa-search" />
                      </span>
                      <span
                        className="icon is-small is-right"
                        role="button"
                        onClick={() => {
                          setQuery("");
                          searchGroups();
                        }}
                        style={{ pointerEvents: "initial", cursor: "pointer" }}
                      >
                        <i className="fas fa-times" />
                      </span>
                    </p>
                    <p className="control">
                      <SecondaryButton text={"検索"} onClick={searchGroups} />
                    </p>
                  </div>
                </div>
              </div>
              <div className="level-right">
                <div className="level-item">
                  <SecondaryButton
                    text={"CSV出力"}
                    onClick={() => onDownloadCsv(orgId)}
                  />
                </div>
                <div className="level-item">
                  <OutsideClickHandler onOutsideClick={dropdownModal.close}>
                    <Dropdown
                      title="一括操作"
                      isActive={dropdownModal.isActive}
                      onClick={dropdownModal.open}
                      menus={[
                        {
                          title: "一括登録",
                          onClick: csvImportModal.open,
                        },
                        {
                          title: "一括更新",
                          onClick: csvUpdateModal.open,
                        },
                      ]}
                    />
                  </OutsideClickHandler>
                </div>
                <div className="level-item">
                  <PrimaryButton
                    text={"新規登録"}
                    onClick={() => {
                      setIsNewGroup(true);
                      groupNameModal.open();
                    }}
                  />
                </div>
              </div>
            </nav>
            <div className="table-container">
              <table className="table is-fullwidth">
                <thead>
                  <tr>
                    <th>グループID</th>
                    <th>グループ名</th>
                    {canIgnoreAclSetting && <th>IPアドレス制御</th>}
                    <th aria-label="ユーザー一覧"></th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group) => (
                    <tr key={group.id} className="c-tr-has-tag">
                      <td>{convertToFormattedId(group.id!)}</td>
                      <td>
                        <div className="group-list-group-name-wrapper">
                          <span className="group-list-group-name">
                            {group.name}
                          </span>
                          <TextButton
                            icon="EditIcon"
                            text="編集"
                            onClick={() => {
                              setSelectedGroupId(group.id!);
                              setIsNewGroup(false);
                              groupNameModal.open();
                            }}
                          />
                        </div>
                      </td>
                      {canIgnoreAclSetting && (
                        <td>{group.isIgnoreAclSetting ? "無効" : "有効"}</td>
                      )}
                      <td>
                        {/* /:groupID/users */}
                        <Link
                          to={ServiceProviderR.GroupUsers.link({
                            orgId: orgId,
                            groupId: group.id!,
                          })}
                        >
                          ユーザー一覧
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: "16px" }}>
                <Pagination
                  onPageClick={movePage}
                  currentPage={paging.current}
                  lastPage={paging.total}
                />
              </div>
            </div>
            {groupNameModal.isActive && (
              <GroupNameModal
                isLoading={isLoading}
                title={
                  isNewGroup ? "グループを作成する" : "グループ名を更新する"
                }
                isActive={groupNameModal.isActive}
                onModal={groupNameModal.close}
                onSubmit={(group) =>
                  isNewGroup
                    ? onCreateGroup(group, orgId)
                    : onUpdateGroup(group, orgId)
                }
                targetGroup={
                  isNewGroup
                    ? null
                    : groups.find((g) => g.id === selectedGroupId)!
                }
                buttonText={isNewGroup ? "作成する" : "更新する"}
                canIgnoreAclSetting={canIgnoreAclSetting}
              />
            )}
            {confirmationModal.isActive && (
              <ConfirmationModal
                isActive={confirmationModal.isActive}
                isLoading={isLoading}
                onModal={confirmationModal.close}
                onSubmit={() => {
                  const targetGroup = groups.find(
                    (g) => g.id === selectedGroupId,
                  )!;
                  onUpdateGroup({ ...targetGroup, email: "" }, orgId);
                  confirmationModal.close();
                }}
                title={"メールアドレスを削除する"}
                body={"メールアドレスを削除しますか?"}
                btnTitle={"削除する"}
              />
            )}
            <BatchImportGroupModal
              orgId={orgId}
              isBatchImporting={isBatchImporting}
              isActive={csvImportModal.isActive}
              onClose={csvImportModal.close}
              onImport={importByCsv}
              onDownloadTemplateCsv={downloadTemplateCsv}
            />
            <BatchImportGroupModal
              orgId={orgId}
              isBatchImporting={isBatchImporting}
              isActive={csvUpdateModal.isActive}
              batchType="UPDATE"
              onClose={csvUpdateModal.close}
              onImport={updateByCsv}
            />
          </div>
        </div>
      </div>
    </ServiceProviderTemplate>
  );
};

export const OrganizationGroupsListContainer = connectPageService(
  OrganizationGroupsList,
  () => new GroupsService(),
);
